// HomeCustomProductsSection.js (Web - MUI)
import React, { useEffect, useState } from 'react';
import {
  Typography, Box, Grid, Card, CardActionArea, CardMedia, CardContent,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip,
  Fade, Grow, TextField, Modal, Backdrop, useMediaQuery, Stack, Tooltip, Alert,
  Snackbar, Divider
} from '@mui/material';
import { useTheme, alpha, keyframes } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PaletteIcon from '@mui/icons-material/Palette';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LayersIcon from '@mui/icons-material/Layers';
import config from './config';
import { useTranslation } from 'react-i18next';
import { useCart } from './CartContext';
import { useCurrency } from './CurrencyContext';
import { getPriceInCurrency, getCurrencyDecimals } from './currencyHelpers';
import { fetchProducts, fetchCategories } from './api';

// ✨ Animations
const slideIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

function shouldDisplayDimensionInitial(val) {
  if (typeof val === 'number') return !isNaN(val) && val !== 0;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    return trimmed.length > 0 && trimmed.toLowerCase() !== 'null' && trimmed.toLowerCase() !== 'undefined' && trimmed !== '0';
  }
  return false;
}

const getImageUrl = (imageData) => {
  const fallbackUrl = '/logo-storesye.png';
  if (!imageData) return fallbackUrl;
  if (typeof imageData === 'object' && !Array.isArray(imageData)) {
    if (imageData.medium) return imageData.medium.startsWith('http') ? imageData.medium : `${config.SITE_BASE_URL}${imageData.medium}`;
    return fallbackUrl;
  }
  if (typeof imageData === 'string') {
    if (imageData.startsWith('/images/')) return `${config.SITE_BASE_URL}${imageData}`;
    if (imageData.startsWith('data:image') || imageData.startsWith('http')) return imageData;
  }
  return fallbackUrl;
};

const getImageUrlLarge = (imageData) => {
  const fallbackUrl = '/logo-storesye.png';
  if (!imageData) return fallbackUrl;
  if (typeof imageData === 'object' && !Array.isArray(imageData)) {
    if (imageData.large) return imageData.large.startsWith('http') ? imageData.large : `${config.SITE_BASE_URL}${imageData.large}`;
    if (imageData.medium) return imageData.medium.startsWith('http') ? imageData.medium : `${config.SITE_BASE_URL}${imageData.medium}`;
    return fallbackUrl;
  }
  if (typeof imageData === 'string') {
    if (imageData.startsWith('/images/')) return `${config.SITE_BASE_URL}${imageData}`;
    if (imageData.startsWith('data:image') || imageData.startsWith('http')) return imageData;
  }
  return fallbackUrl;
};

export default function HomeCustomProductsSection() {
  const { addToCart, selectedBranch, cartItems = [] } = useCart();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [selectedSubProducts, setSelectedSubProducts] = useState([]);
  const [customTotal, setCustomTotal] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [duplicateProductName, setDuplicateProductName] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const currencySymbol = getCurrencySymbol(selectedCurrency);
  const decimals = getCurrencyDecimals(selectedCurrency);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const isProductInCart = (productId) => cartItems.some(item => item.Id === productId && item.isCustom);

  useEffect(() => {
    if (!selectedBranch) return;
    setLoading(true);
    Promise.all([
      fetchProducts(selectedBranch.id, selectedCurrency),
      fetchCategories(selectedBranch.id)
    ]).then(([productsData, catsData]) => {
      const customProducts = productsData.filter(p => Array.isArray(p.SubProducts) && p.SubProducts.length > 0);
      setProducts(customProducts);
      const filtered = catsData
        .filter(cat => !/^(العروض:|offer|deal|مجموعة:)/i.test(cat.CategoryName))
        .sort((a, b) => (a.CategoriesOrder ?? 9999) - (b.CategoriesOrder ?? 9999));
      const catsWithProducts = filtered.filter(cat =>
        customProducts.some(p => p.CategoryId === cat.Id)
      );
      setCategories(catsWithProducts);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [selectedBranch, selectedCurrency]);

  const handleImageClick = (imageData, e) => {
    if (e) e.stopPropagation();
    setSelectedImage(getImageUrlLarge(imageData));
  };

  const handleCustomize = (product) => {
    if (isProductInCart(product.Id)) {
      setDuplicateProductName(product.ProductName);
      setShowDuplicateAlert(true);
      return;
    }
    setSelectedProduct(product);
    const filteredSubs = product.SubProducts.filter(sp => sp.IsActive !== false);
    setSelectedSubProducts(filteredSubs.map(sp => ({
      ...sp, selected: false, Height: sp.Height ?? '', Width: sp.Width ?? '',
      Quantity: sp.FixedQuantity ? 1 : (sp.Quantity || 0),
      showHeightInitial: shouldDisplayDimensionInitial(sp.Height),
      showWidthInitial: shouldDisplayDimensionInitial(sp.Width)
    })));
    const productColors = product.ProductColors || [];
    setSelectedColor(productColors.length === 1 ? productColors[0] : null);
    setCustomizeOpen(true);
    setCustomTotal(getPriceInCurrency(product, selectedCurrency));
  };

  const handleSubProductToggle = (idx) => {
    setSelectedSubProducts(subs => {
      const updated = subs.map((sp, i) => {
        if (i !== idx) return sp;
        const nextSelected = !sp.selected;
        if (sp.FixedQuantity) return { ...sp, selected: nextSelected, Quantity: nextSelected ? 1 : 0 };
        return { ...sp, selected: nextSelected, Quantity: nextSelected ? (sp.Quantity && sp.Quantity > 0 ? sp.Quantity : 1) : 0 };
      });
      recalculateTotal(updated);
      return updated;
    });
  };

  const handleIncrement = (idx) => {
    setSelectedSubProducts(subs => {
      const updated = subs.map((sp, i) => {
        if (i !== idx || sp.FixedQuantity) return sp;
        const qty = Number(sp.Quantity || 0) + 1;
        return { ...sp, Quantity: qty, selected: qty > 0 };
      });
      recalculateTotal(updated);
      return updated;
    });
  };

  const handleDecrement = (idx) => {
    setSelectedSubProducts(subs => {
      const updated = subs.map((sp, i) => {
        if (i !== idx || sp.FixedQuantity) return sp;
        const qty = Math.max(0, Number(sp.Quantity || 0) - 1);
        return { ...sp, Quantity: qty, selected: qty > 0 };
      });
      recalculateTotal(updated);
      return updated;
    });
  };

  const handleSubProductFieldChange = (idx, field, value) => {
    setSelectedSubProducts(subs => {
      const updated = subs.map((sp, i) => {
        if (i !== idx) return sp;
        if (field === 'Quantity' && sp.FixedQuantity) return sp;
        return { ...sp, [field]: value };
      });
      if (field === 'Quantity') {
        updated.forEach((u, i) => { if (i === idx) u.selected = Number(u.Quantity || 0) > 0; });
        recalculateTotal(updated);
      }
      return updated;
    });
  };

  const recalculateTotal = (subs) => {
    let total = selectedProduct ? getPriceInCurrency(selectedProduct, selectedCurrency) : 0;
    subs.forEach(sp => {
      if (sp.selected) {
        total += getPriceInCurrency(sp, selectedCurrency) * Number(sp.Quantity || 0);
      }
    });
    setCustomTotal(total);
  };

  const handleAddToCart = () => {
    const productColors = selectedProduct?.ProductColors || [];
    if (productColors.length > 0 && !selectedColor) {
      showSnackbar(t('pleaseSelectColor', 'Please select a color first!'), 'warning');
      return;
    }
    const chosenSubs = selectedSubProducts.filter(sp => sp.selected).map(sp => ({
      Id: sp.Id, Name: sp.Name, Description: sp.Description,
      Price: getPriceInCurrency(sp, selectedCurrency),
      CurrencyPrices: sp.CurrencyPrices, SizeType: sp.SizeType,
      Height: sp.Height, Width: sp.Width,
      Quantity: sp.Quantity || 1, quantity: sp.Quantity || 1,
      ImageUrl: sp.ImageUrl, IsActive: sp.IsActive
    }));
    addToCart({
      ...selectedProduct,
      Price: getPriceInCurrency(selectedProduct, selectedCurrency),
      Currency: selectedCurrency, CurrencyPrices: selectedProduct.CurrencyPrices,
      SubProducts: chosenSubs, isCustom: true,
      CustomTotal: customTotal, SelectedColor: selectedColor?.colorName || null
    });
    showSnackbar(t('cart.itemAdded', 'Added to cart'), 'success');
    handleClose();
  };

  const handleClose = () => {
    setCustomizeOpen(false);
    setSelectedProduct(null);
    setSelectedSubProducts([]);
    setSelectedColor(null);
    setCustomTotal(0);
  };

  // Don't render if loading or no custom products
  if (loading || products.length === 0 || categories.length === 0) {
    return null;
  }

  return (
    <>
      {/* ─── Section Title ─── */}
      <Box sx={{ 
        display: 'flex', alignItems: 'center', justifyContent: 'center', 
        mb: { xs: 3, sm: 4, md: 5 }, mt: { xs: 4, sm: 6, md: 8 } 
      }}>
        <Box sx={{ position: 'relative', textAlign: 'center', px: { xs: 1.5, sm: 5 }, py: { xs: 0.8, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 0.5 }}>
            <TuneIcon sx={{ fontSize: { xs: 22, sm: 28 }, color: theme.palette.primary.main }} />
            <Typography
              variant="h4"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 800,
                fontSize: { xs: '1.25rem', sm: '2rem', md: '2.25rem' },
                letterSpacing: '0.5px',
                display: 'inline-block',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: { xs: -4, sm: -8 },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: { xs: '35px', sm: '60px' },
                  height: { xs: '2.5px', sm: '4px' },
                  borderRadius: '2px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`
                }
              }}
            >
              {t('customProducts.title', 'Customizable Products')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ─── Product Categories & Cards ─── */}
      {categories.map((cat, catIndex) => {
        const catProducts = products.filter(p => p.CategoryId === cat.Id);
        if (catProducts.length === 0) return null;

        return (
          <Box
            key={cat.Id}
            sx={{
              mb: { xs: 5, sm: 8, md: 10 },
              animation: `${slideIn} 0.6s ease-out`,
              animationDelay: `${catIndex * 0.1}s`,
              animationFillMode: 'backwards'
            }}
          >
            {/* Category Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 2.5, sm: 4, md: 5 } }}>
              <Box sx={{ position: 'relative', textAlign: 'center', px: { xs: 1.5, sm: 5 }, py: { xs: 0.8, sm: 2 } }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 800,
                    fontSize: { xs: '1.25rem', sm: '2rem', md: '2.25rem' },
                    letterSpacing: '0.5px',
                    textTransform: 'capitalize',
                    display: 'inline-block',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: { xs: -4, sm: -8 },
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: { xs: '35px', sm: '60px' },
                      height: { xs: '2.5px', sm: '4px' },
                      borderRadius: '2px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`
                    }
                  }}
                >
                  {cat.CategoryName}
                </Typography>
              </Box>
            </Box>

            {/* Products Grid - matches Products.js card style */}
            <Grid container spacing={{ xs: 1, sm: 2.5, md: 3 }} sx={{ direction: 'rtl', justifyContent: 'center' }}>
              {catProducts.map((prod, prodIndex) => {
                const imageUrl = getImageUrl(prod.ImageUrl);
                const inCart = isProductInCart(prod.Id);
                const hasColors = prod.ProductColors && Array.isArray(prod.ProductColors) && prod.ProductColors.length > 0;
                const basePrice = getPriceInCurrency(prod, selectedCurrency);
                const subCount = prod.SubProducts?.length || 0;

                return (
                  <Grid
                    item
                    key={prod.Id}
                    sx={{
                      animation: `${slideIn} 0.5s ease-out`,
                      animationDelay: `${prodIndex * 0.05}s`,
                      animationFillMode: 'backwards'
                    }}
                  >
                    <Card
                      sx={{
                        width: { xs: 140, sm: 200, md: 220 },
                        height: '100%',
                        borderRadius: { xs: 1.5, sm: 2 },
                        overflow: 'visible',
                        boxShadow: `0 ${isMobile ? '3px 10px' : '8px 24px'} ${alpha(theme.palette.common.black, 0.08)}`,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        bgcolor: 'background.paper',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        opacity: inCart ? 0.6 : 1,
                        '&:hover': {
                          transform: inCart ? 'none' : (isMobile ? 'scale(0.98)' : 'translateY(-12px)'),
                          boxShadow: inCart
                            ? `0 ${isMobile ? '3px 10px' : '8px 24px'} ${alpha(theme.palette.common.black, 0.08)}`
                            : `0 ${isMobile ? '5px 14px' : '20px 40px'} ${alpha(theme.palette.primary.main, 0.2)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, inCart ? 0.1 : 0.3)}`,
                          '& .product-image': { transform: inCart ? 'scale(1)' : 'scale(1.1)' }
                        },
                        '&:active': {
                          transform: isMobile ? 'scale(0.95)' : 'none',
                        }
                      }}
                    >
                      {/* In Cart Badge */}
                      {inCart && (
                        <Chip
                          label={t('customProducts.inCart', 'In Cart')}
                          icon={<ShoppingCartIcon sx={{ fontSize: { xs: 10, sm: 14 }, color: 'white !important' }} />}
                          sx={{
                            position: 'absolute',
                            top: { xs: 4, sm: 10 },
                            left: { xs: 4, sm: 10 },
                            bgcolor: alpha(theme.palette.warning.main, 0.95),
                            color: 'white',
                            fontWeight: 700,
                            fontSize: { xs: '8px', sm: '11px' },
                            height: { xs: 20, sm: 26 },
                            zIndex: 3,
                            boxShadow: 2,
                            '& .MuiChip-icon': { color: 'white' }
                          }}
                        />
                      )}

                      <CardActionArea
                        onClick={() => handleCustomize(prod)}
                        disabled={inCart}
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%', cursor: inCart ? 'not-allowed' : 'pointer' }}
                      >
                        {/* Image Section */}
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            height: { xs: 100, sm: 160, md: 180 },
                            overflow: 'hidden',
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                            borderRadius: '12px 12px 0 0'
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={imageUrl}
                            alt={prod.ProductName}
                            loading="lazy"
                            className="product-image"
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                            onError={(e) => { e.target.src = '/logo-storesye.png'; }}
                          />

                          {/* Customizable Badge */}
                          {!inCart && (
                            <Chip
                              icon={<TuneIcon sx={{ fontSize: { xs: 10, sm: 14 }, color: 'white !important' }} />}
                              label={t('customizable', 'Custom')}
                              sx={{
                                position: 'absolute',
                                top: { xs: 4, sm: 10 },
                                right: { xs: 4, sm: 10 },
                                bgcolor: alpha(theme.palette.primary.main, 0.95),
                                color: 'white',
                                fontWeight: 700,
                                fontSize: { xs: '8px', sm: '11px' },
                                height: { xs: 20, sm: 26 },
                                zIndex: 2,
                                boxShadow: 2,
                                '& .MuiChip-icon': { color: 'white' }
                              }}
                            />
                          )}

                          {/* Colors Badge */}
                          {hasColors && !inCart && (
                            <Chip
                              icon={<PaletteIcon sx={{ fontSize: { xs: 9, sm: 12 }, color: 'white !important' }} />}
                              label={`${prod.ProductColors.length}`}
                              sx={{
                                position: 'absolute',
                                bottom: { xs: 4, sm: 10 },
                                right: { xs: 4, sm: 10 },
                                bgcolor: alpha(theme.palette.secondary.main, 0.9),
                                color: 'white',
                                fontWeight: 700,
                                fontSize: { xs: '8px', sm: '10px' },
                                height: { xs: 18, sm: 22 },
                                zIndex: 2,
                                boxShadow: 2,
                                '& .MuiChip-icon': { color: 'white' }
                              }}
                            />
                          )}

                          {/* Sub-products count badge */}
                          {subCount > 0 && !inCart && (
                            <Chip
                              icon={<LayersIcon sx={{ fontSize: { xs: 9, sm: 12 }, color: 'white !important' }} />}
                              label={`${subCount}`}
                              sx={{
                                position: 'absolute',
                                bottom: { xs: 4, sm: 10 },
                                left: { xs: 4, sm: 10 },
                                bgcolor: 'rgba(0,0,0,0.65)',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: { xs: '8px', sm: '10px' },
                                height: { xs: 18, sm: 22 },
                                zIndex: 2,
                                '& .MuiChip-icon': { color: 'white' }
                              }}
                            />
                          )}
                        </Box>

                        {/* Content Section */}
                        <CardContent sx={{ p: { xs: 1, sm: 2 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Tooltip title={prod.ProductName} arrow>
                            <Typography
                              variant="h6"
                              sx={{
                                fontSize: { xs: '11px', sm: '15px', md: '16px' },
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                mb: { xs: 0.2, sm: 0.5 },
                                textAlign: 'center',
                                lineHeight: 1.3,
                                height: { xs: '2.3em', sm: '2.6em' },
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {prod.ProductName}
                            </Typography>
                          </Tooltip>

                          {prod.Description && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: '8px', sm: '11px' },
                                color: theme.palette.text.secondary,
                                mb: { xs: 0.8, sm: 1.5 },
                                textAlign: 'center',
                                lineHeight: 1.3,
                                height: { xs: '2.3em', sm: '2.8em' },
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {prod.Description}
                            </Typography>
                          )}

                          <Box sx={{ flexGrow: 1 }} />

                          {/* Price */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 1, sm: 2 } }}>
                            <Typography sx={{ color: theme.palette.primary.main, fontWeight: 800, fontSize: { xs: '11px', sm: '16px' } }}>
                              {t('customProducts.startingFrom', 'From')} {basePrice.toFixed(decimals)} {currencySymbol}
                            </Typography>
                          </Box>

                          {/* Action Button */}
                          <Box sx={{ width: '100%', mt: 'auto' }}>
                            {!inCart ? (
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={(e) => { e.stopPropagation(); handleCustomize(prod); }}
                                sx={{
                                  bgcolor: theme.palette.primary.main,
                                  borderRadius: { xs: 1.5, sm: 3 },
                                  color: theme.palette.primary.contrastText,
                                  py: { xs: 0.7, sm: 1.2 },
                                  fontSize: { xs: '9px', sm: '13px' },
                                  fontWeight: 700,
                                  textTransform: 'none',
                                  boxShadow: `0 ${isMobile ? '2px 6px' : '4px 12px'} ${alpha(theme.palette.primary.main, 0.3)}`,
                                  '&:hover': {
                                    bgcolor: theme.palette.primary.dark,
                                    boxShadow: `0 ${isMobile ? '3px 10px' : '6px 16px'} ${alpha(theme.palette.primary.main, 0.4)}`,
                                    transform: isMobile ? 'none' : 'translateY(-2px)'
                                  },
                                  '&:active': { transform: 'scale(0.95)' },
                                  transition: 'all 0.3s ease',
                                  gap: { xs: 0.4, sm: 1 }
                                }}
                              >
                                <TuneIcon sx={{ fontSize: { xs: 12, sm: 18 } }} />
                                {t('customizeProduct', 'Customize')}
                              </Button>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: alpha(theme.palette.success.main, 0.08),
                                  borderRadius: { xs: 1.5, sm: 3 },
                                  py: { xs: 0.8, sm: 1.2 },
                                  border: `${isMobile ? '1.5px' : '2px'} solid ${alpha(theme.palette.success.main, 0.2)}`,
                                  gap: 0.5
                                }}
                              >
                                <CheckCircleIcon sx={{ fontSize: { xs: 12, sm: 16 }, color: theme.palette.success.main }} />
                                <Typography sx={{ color: theme.palette.success.main, fontWeight: 700, fontSize: { xs: '9px', sm: '12px' } }}>
                                  {t('customProducts.inCart', 'In Cart')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}

              {catProducts.length === 0 && (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: { xs: 5, sm: 8 }, px: 2, borderRadius: { xs: 2, sm: 4 }, bgcolor: alpha(theme.palette.primary.main, 0.03), border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}` }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                      {t('customProducts.noProducts', 'No products')}
                                        </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        );
      })}

      {/* ─── Customization Dialog ─── */}
      <Dialog
        open={customizeOpen}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '24px', maxHeight: '90vh', bgcolor: 'background.paper' } }}
        TransitionComponent={Fade}
        transitionDuration={400}
      >
        <DialogTitle sx={{
          fontWeight: 800,
          fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.75rem' },
          color: theme.palette.primary.main,
          pb: { xs: 1.5, sm: 2 },
          pt: { xs: 2, sm: 2.5, md: 3 },
          px: { xs: 2, sm: 2.5, md: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
            <TuneIcon sx={{ fontSize: { xs: 22, sm: 26, md: 28 }, color: theme.palette.primary.main }} />
            <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
              {selectedProduct?.ProductName || t('customizeProduct', 'Customize Your Product')}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size={isMobile ? "small" : "medium"}
            sx={{ color: theme.palette.text.secondary, '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main } }}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mt: { xs: 1, sm: 1.5, md: 2 } }}>
          {selectedProduct && (
            <>
              {/* Product Image in Dialog */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: { xs: 2, sm: 3 },
                  cursor: 'zoom-in'
                }}
                onClick={(e) => handleImageClick(selectedProduct.ImageUrl, e)}
              >
                <Box
                  component="img"
                  src={getImageUrlLarge(selectedProduct.ImageUrl)}
                  alt={selectedProduct.ProductName}
                  sx={{
                    maxWidth: { xs: '100%', sm: '60%', md: '50%' },
                    maxHeight: { xs: 200, sm: 280, md: 320 },
                    objectFit: 'contain',
                    borderRadius: 3,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`,
                    border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'scale(1.03)' }
                  }}
                  onError={(e) => { e.target.src = '/logo-storesye.png'; }}
                />
              </Box>

              {/* Product Description */}
              {selectedProduct.Description && (
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: 'center',
                    mb: { xs: 2, sm: 3 },
                    fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                    lineHeight: 1.6
                  }}
                >
                  {selectedProduct.Description}
                </Typography>
              )}

              {/* Base Price */}
              <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
                <Typography sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 800,
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' }
                }}>
                  {t('customProducts.startingFrom', 'From')}: {getPriceInCurrency(selectedProduct, selectedCurrency).toFixed(decimals)} {currencySymbol}
                </Typography>
              </Box>

              {/* Colors Section */}
              {selectedProduct.ProductColors && Array.isArray(selectedProduct.ProductColors) && selectedProduct.ProductColors.length > 0 && (
                <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <PaletteIcon sx={{ color: 'secondary.main', fontSize: { xs: 20, sm: 24 } }} />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}
                    >
                      {t('selectColor', 'Select Color')}
                    </Typography>
                    {selectedColor && (
                      <Chip
                        label={selectedColor.colorName}
                        color="secondary"
                        size="small"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                    )}
                    <Chip
                      label={selectedProduct.ProductColors.length === 1 ? t('autoSelected', 'Auto-selected') : `${selectedProduct.ProductColors.length} ${t('options', 'options')}`}
                      size="small"
                      variant="outlined"
                      color="secondary"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                    />
                  </Stack>

                  {!selectedColor && selectedProduct.ProductColors.length > 1 && (
                    <Alert severity="warning" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                      ⚠️ {t('pleaseSelectColorBeforeAdding', 'Please select a color before adding to cart')}
                    </Alert>
                  )}

                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    {selectedProduct.ProductColors.map((color, index) => {
                      const isSelected = selectedColor?.colorName === color.colorName;
                      let colorImgUrl = null;
                      if (color.colorImage) {
                        if (typeof color.colorImage === 'object' && color.colorImage !== null) {
                          colorImgUrl = color.colorImage.medium || color.colorImage.large || color.colorImage.thumbnail;
                        } else if (typeof color.colorImage === 'string') {
                          colorImgUrl = color.colorImage.startsWith('http')
                            ? color.colorImage
                            : `${config.SITE_BASE_URL}${color.colorImage}`;
                        }
                      }

                      return (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Tooltip title={color.colorName} arrow>
                            <Card
                              onClick={() => {
                                setSelectedColor(color);
                                showSnackbar(`${t('products.colorSelected', 'Color selected')}: ${color.colorName}`, 'success');
                              }}
                              sx={{
                                cursor: 'pointer',
                                border: `3px solid ${isSelected ? theme.palette.secondary.main : alpha(theme.palette.divider, 0.3)}`,
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                boxShadow: isSelected ? `0 6px 20px ${alpha(theme.palette.secondary.main, 0.4)}` : 2,
                                position: 'relative',
                                overflow: 'hidden',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                  boxShadow: 4,
                                  borderColor: theme.palette.secondary.main
                                },
                                '&:active': { transform: 'scale(0.95)' }
                              }}
                            >
                              {colorImgUrl ? (
                                <CardMedia
                                  component="img"
                                  image={colorImgUrl}
                                  alt={color.colorName}
                                  sx={{ height: { xs: 80, sm: 100 }, objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:${isMobile ? '80px' : '100px'};font-size:2rem;">🎨</div>`;
                                  }}
                                />
                              ) : (
                                <Box sx={{
                                  height: { xs: 80, sm: 100 },
                                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                  <PaletteIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: alpha(theme.palette.secondary.main, 0.3) }} />
                                </Box>
                              )}

                              <CardContent sx={{
                                p: { xs: 1, sm: 1.5 },
                                bgcolor: isSelected ? 'secondary.main' : alpha(theme.palette.common.black, 0.7),
                                textAlign: 'center'
                              }}>
                                <Typography variant="caption" fontWeight="bold" sx={{
                                  color: 'white',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block'
                                }}>
                                  {color.colorName}
                                </Typography>
                              </CardContent>

                              {isSelected && (
                                <Box sx={{
                                  position: 'absolute', top: 8, right: 8,
                                  bgcolor: 'secondary.main', borderRadius: '50%',
                                  width: { xs: 24, sm: 28 }, height: { xs: 24, sm: 28 },
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  boxShadow: 2, zIndex: 1
                                }}>
                                  <CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: 'white' }} />
                                </Box>
                              )}
                            </Card>
                          </Tooltip>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Divider sx={{ my: { xs: 3, sm: 4 } }} />
                </Box>
              )}

              {/* Subproducts Header */}
              {selectedSubProducts.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: { xs: 2, sm: 3 } }}>
                  <LayersIcon sx={{ color: theme.palette.primary.main, fontSize: { xs: 20, sm: 24 } }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                    {t('customProducts.selectComponents', 'Select Components')} ({selectedSubProducts.filter(sp => sp.selected).length}/{selectedSubProducts.length})
                  </Typography>
                </Stack>
              )}

              {/* Subproducts Grid */}
              {selectedProduct.SubProducts && selectedProduct.SubProducts.length > 0 ? (
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }} justifyContent="center">
                  {selectedSubProducts.map((sp, idx) => {
                    const spImageUrl = getImageUrl(sp.ImageUrl);
                    const spPrice = getPriceInCurrency(sp, selectedCurrency);

                    return (
                      <Grid item xs={6} sm={6} md={4} lg={3} key={sp.Id || idx} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Grow in timeout={300 + idx * 50}>
                          <Card sx={{
                            width: '100%',
                            maxWidth: { xs: 170, sm: 200, md: 240 },
                            minHeight: { xs: 280, sm: 300, md: 320 },
                            borderRadius: { xs: '16px', sm: '18px', md: '20px' },
                            boxShadow: sp.selected
                              ? `0 ${isMobile ? '8px 20px' : '12px 32px'} ${alpha(theme.palette.primary.main, 0.25)}`
                              : `0 ${isMobile ? '2px 8px' : '4px 12px'} ${alpha(theme.palette.common.black, 0.08)}`,
                            border: sp.selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            bgcolor: 'background.paper',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'visible',
                            '&:hover': {
                              boxShadow: `0 ${isMobile ? '10px 24px' : '16px 40px'} ${alpha(theme.palette.primary.main, 0.2)}`,
                              transform: isMobile ? 'scale(0.98)' : 'translateY(-4px)',
                              borderColor: theme.palette.primary.main,
                              '& .subproduct-image': { transform: 'scale(1.1)' },
                              '& .sub-hover-overlay': { opacity: isMobile ? 0 : 1 }
                            },
                            '&:active': { transform: 'scale(0.95)' }
                          }}>
                            <CardActionArea
                              onClick={() => !sp.selected && handleSubProductToggle(idx)}
                              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%', p: 0 }}
                            >
                              {sp.ImageUrl && (
                                <Box sx={{
                                  position: 'relative',
                                  width: '100%',
                                  height: { xs: 80, sm: 90, md: 100 },
                                  overflow: 'hidden',
                                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                                  cursor: 'zoom-in'
                                }} onClick={(e) => handleImageClick(sp.ImageUrl, e)}>
                                  <CardMedia
                                    component="img"
                                    image={spImageUrl}
                                    alt={sp.Name}
                                    className="subproduct-image"
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px 20px 0 0', transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}
                                  />
                                  {!isMobile && (
                                    <Box className="sub-hover-overlay" sx={{
                                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                      background: `linear-gradient(to top, ${alpha(theme.palette.common.black, 0.6)}, transparent)`,
                                      opacity: 0, transition: 'opacity 0.3s ease',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'
                                    }}>
                                      <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Click to Zoom</Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}

                              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 0.8, sm: 1 }, flexGrow: 1 }}>
                                <Typography variant="subtitle2" sx={{
                                  fontWeight: 700,
                                  textAlign: 'center',
                                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                                  color: sp.selected ? theme.palette.primary.main : theme.palette.text.primary,
                                  lineHeight: 1.3,
                                  minHeight: '2.6em',
                                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                                }}>
                                  {sp.Name}
                                </Typography>

                                {spPrice > 0 && (
                                  <Typography variant="body2" sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' },
                                    color: theme.palette.primary.main
                                  }}>
                                    {spPrice.toFixed(decimals)} {currencySymbol}
                                  </Typography>
                                )}

                                {sp.Description && (
                                  <Typography variant="caption" sx={{
                                    color: theme.palette.text.secondary,
                                    textAlign: 'center',
                                    fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                    lineHeight: 1.4,
                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                                  }}>
                                    {sp.Description}
                                  </Typography>
                                )}

                                {sp.SizeType && (
                                  <Chip
                                    label={`${t('customProducts.sizeType', 'Size')}: ${sp.SizeType}`}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(theme.palette.info.main, 0.1),
                                      color: theme.palette.info.main,
                                      fontWeight: 600,
                                      fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                                      height: { xs: 20, sm: 22, md: 24 }
                                    }}
                                  />
                                )}

                                {(sp.showHeightInitial || sp.showWidthInitial) && (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.8, sm: 1 }, width: '100%', mt: { xs: 0.5, sm: 1 } }} onClick={(e) => e.stopPropagation()}>
                                    {sp.showHeightInitial && (
                                      <TextField
                                        label={t('customProducts.height', 'Height')}
                                        type="text"
                                        size="small"
                                        value={sp.Height}
                                        onChange={e => handleSubProductFieldChange(idx, 'Height', e.target.value)}
                                        disabled={!sp.selected}
                                        fullWidth
                                        sx={{
                                          '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, textAlign: 'center', py: { xs: 0.8, sm: 1 } },
                                          '& .MuiInputLabel-root': { fontSize: { xs: '0.75rem', sm: '0.85rem' } }
                                        }}
                                      />
                                    )}
                                    {sp.showWidthInitial && (
                                      <TextField
                                        label={t('customProducts.width', 'Width')}
                                        type="text"
                                        size="small"
                                        value={sp.Width}
                                        onChange={e => handleSubProductFieldChange(idx, 'Width', e.target.value)}
                                        disabled={!sp.selected}
                                        fullWidth
                                        sx={{
                                          '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' }, textAlign: 'center', py: { xs: 0.8, sm: 1 } },
                                          '& .MuiInputLabel-root': { fontSize: { xs: '0.75rem', sm: '0.85rem' } }
                                        }}
                                      />
                                    )}
                                  </Box>
                                )}

                                {/* Quantity Controls */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1 }, mt: 'auto', pt: { xs: 1.5, sm: 2 }, width: '100%', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                                  {!sp.selected ? (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      fullWidth
                                      onClick={() => handleSubProductToggle(idx)}
                                      sx={{
                                        borderRadius: { xs: '10px', sm: '12px' },
                                        fontWeight: 700,
                                        fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                                        py: { xs: 0.8, sm: 1 },
                                        bgcolor: theme.palette.primary.main,
                                        color: theme.palette.primary.contrastText,
                                        '&:hover': { bgcolor: theme.palette.primary.dark, transform: 'scale(1.02)' },
                                        '&:active': { transform: 'scale(0.98)' },
                                        transition: 'all 0.2s ease'
                                      }}
                                    >
                                      {t('addToCart', 'Add')}
                                    </Button>
                                  ) : sp.FixedQuantity ? (
                                    <Chip
                                      label={t('customProducts.fixedQuantity', 'Fixed Quantity')}
                                      sx={{
                                        bgcolor: alpha(theme.palette.success.main, 0.1),
                                        color: theme.palette.success.main,
                                        fontWeight: 700,
                                        fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                        height: { xs: 22, sm: 24, md: 26 }
                                      }}
                                    />
                                  ) : (
                                    <Box sx={{
                                      display: 'flex', alignItems: 'center',
                                      gap: { xs: 0.5, sm: 0.8, md: 1 },
                                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                                      borderRadius: { xs: '10px', sm: '12px' },
                                      p: { xs: 0.4, sm: 0.5 },
                                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                                    }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDecrement(idx)}
                                        disabled={Number(sp.Quantity || 0) <= 0}
                                        sx={{
                                          bgcolor: theme.palette.error.main, color: 'white',
                                          width: { xs: 24, sm: 26, md: 28 }, height: { xs: 24, sm: 26, md: 28 },
                                          '&:hover': { bgcolor: theme.palette.error.dark, transform: 'scale(1.1)' },
                                          '&:disabled': { bgcolor: alpha(theme.palette.error.main, 0.3) },
                                          '&:active': { transform: 'scale(0.9)' },
                                          transition: 'all 0.2s ease'
                                        }}
                                      >
                                        <RemoveIcon sx={{ fontSize: { xs: 14, sm: 15, md: 16 } }} />
                                      </IconButton>
                                      <Typography sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                                        minWidth: { xs: '26px', sm: '28px', md: '32px' },
                                        textAlign: 'center',
                                        color: theme.palette.primary.main
                                      }}>
                                        {sp.Quantity}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleIncrement(idx)}
                                        sx={{
                                          bgcolor: theme.palette.success.main, color: 'white',
                                          width: { xs: 24, sm: 26, md: 28 }, height: { xs: 24, sm: 26, md: 28 },
                                          '&:hover': { bgcolor: theme.palette.success.dark, transform: 'scale(1.1)' },
                                          '&:active': { transform: 'scale(0.9)' },
                                          transition: 'all 0.2s ease'
                                        }}
                                      >
                                        <AddIcon sx={{ fontSize: { xs: 14, sm: 15, md: 16 } }} />
                                      </IconButton>
                                    </Box>
                                  )}
                                </Box>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grow>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 7, md: 8 }, px: 2 }}>
                  <Typography variant="h6" sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                  }}>
                    {t('noSubproducts', 'No subproducts available.')}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        {/* Dialog Footer */}
        <DialogActions sx={{
          px: { xs: 2, sm: 2.5, md: 3 },
          py: { xs: 2, sm: 2.2, md: 2.5 },
          borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 2 },
          alignItems: 'stretch'
        }}>
          {/* Selected Color Display */}
          {selectedProduct?.ProductColors && selectedProduct.ProductColors.length > 0 && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <PaletteIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                {selectedColor
                  ? `${t('selectedColor', 'Color')}: ${selectedColor.colorName}`
                  : t('noColorSelected', 'No color selected')}
              </Typography>
            </Box>
          )}

          {/* Total */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1 },
            flex: { xs: '1 1 auto', sm: '0 0 auto' },
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'center', sm: 'flex-start' }
          }}>
            <Typography variant="h5" sx={{
              fontWeight: 800, color: theme.palette.primary.main,
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' }
            }}>
              {t('customProducts.total', 'Total')}:
            </Typography>
            <Typography variant="h5" sx={{
              fontWeight: 800, color: theme.palette.primary.main,
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' }
            }}>
              {Number(customTotal || 0).toFixed(decimals)} {currencySymbol}
            </Typography>
          </Box>

          {/* Buttons */}
          <Box sx={{
            display: 'flex', gap: { xs: 1.2, sm: 1.5 },
            flex: { xs: '1 1 auto', sm: '0 0 auto' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              size={isMobile ? "medium" : "large"}
              sx={{
                flex: { xs: 1, sm: '0 0 auto' },
                borderRadius: { xs: '10px', sm: '12px' },
                fontWeight: 700,
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 1, sm: 1.1, md: 1.2 },
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': { borderColor: theme.palette.primary.dark, bgcolor: alpha(theme.palette.primary.main, 0.05) },
                '&:active': {                  transform: 'scale(0.98)'
                }
              }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleAddToCart}
              variant="contained"
              disabled={
                selectedSubProducts.filter(sp => sp.selected).length === 0 ||
                (selectedProduct?.ProductColors && selectedProduct.ProductColors.length > 0 && !selectedColor)
              }
              size={isMobile ? "medium" : "large"}
              sx={{
                flex: { xs: 1, sm: '0 0 auto' },
                borderRadius: { xs: '10px', sm: '12px' },
                fontWeight: 700,
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 1, sm: 1.1, md: 1.2 },
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                boxShadow: `0 ${isMobile ? '2px 8px' : '4px 12px'} ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                  boxShadow: `0 ${isMobile ? '3px 10px' : '6px 16px'} ${alpha(theme.palette.primary.main, 0.4)}`,
                  transform: isMobile ? 'none' : 'translateY(-2px)'
                },
                '&:disabled': { bgcolor: alpha(theme.palette.primary.main, 0.3) },
                '&:active': { transform: 'scale(0.98)' },
                transition: 'all 0.3s ease',
                gap: { xs: 0.5, sm: 0.8, md: 1 }
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: { xs: 18, sm: 19, md: 20 } }} />
              {t('customProducts.addToCart', 'Add to Cart')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ─── Duplicate Product Alert Dialog ─── */}
      <Dialog
        open={showDuplicateAlert}
        onClose={() => setShowDuplicateAlert(false)}
        PaperProps={{
          sx: {
            borderRadius: { xs: '16px', sm: '18px', md: '20px' },
            padding: { xs: 1.5, sm: 2 },
            maxWidth: { xs: 340, sm: 380, md: 400 },
            mx: { xs: 2, sm: 'auto' }
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 },
          color: theme.palette.warning.main, fontWeight: 700,
          fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.4rem' },
          px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }
        }}>
          <WarningAmberIcon sx={{ fontSize: { xs: 28, sm: 30, md: 32 }, color: theme.palette.warning.main }} />
          {t('customProducts.productInCart', 'Product Already in Cart')}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 2.5 } }}>
          <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}>
            {t('customProducts.duplicateMessage', 'The product')} "<strong>{duplicateProductName}</strong>" {t('customProducts.alreadyInCart', 'is already in your cart.')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' } }}>
            {t('customProducts.finishOrderFirst', 'Please complete your current order first, then you can customize and add this product again.')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 2.5, md: 3 }, pb: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button
            onClick={() => setShowDuplicateAlert(false)}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{
              borderRadius: { xs: '10px', sm: '12px' },
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              px: { xs: 2, sm: 2.5 }
            }}
          >
            {t('common.close', 'Close')}
          </Button>
          <Button
            onClick={() => { setShowDuplicateAlert(false); window.location.href = '/cart'; }}
            variant="contained"
            size={isMobile ? "small" : "medium"}
            sx={{
              borderRadius: { xs: '10px', sm: '12px' },
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              px: { xs: 2, sm: 2.5 }
            }}
          >
            {t('common.viewCart', 'View Cart')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Image Zoom Modal ─── */}
      <Modal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backdropFilter: 'blur(8px)', backgroundColor: alpha(theme.palette.common.black, 0.8) }
        }}
      >
        <Fade in={!!selectedImage}>
          <Box sx={{
            position: 'absolute',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: { xs: '95vw', sm: '90vw' },
            maxHeight: { xs: '95vh', sm: '90vh' },
            outline: 'none',
            borderRadius: { xs: '16px', sm: '20px', md: '24px' },
            overflow: 'hidden',
            boxShadow: `0 ${isMobile ? '16px 32px' : '24px 48px'} ${alpha(theme.palette.common.black, 0.4)}`,
            animation: 'modalZoomIn 0.3s ease-out',
            '@keyframes modalZoomIn': {
              from: { transform: 'translate(-50%, -50%) scale(0.8)', opacity: 0 },
              to: { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 }
            }
          }}>
            <IconButton
              onClick={() => setSelectedImage(null)}
              size={isMobile ? "small" : "medium"}
              sx={{
                position: 'absolute',
                top: { xs: 12, sm: 14, md: 16 },
                right: { xs: 12, sm: 14, md: 16 },
                bgcolor: alpha(theme.palette.common.white, 0.95),
                backdropFilter: 'blur(10px)',
                zIndex: 1,
                width: { xs: 36, sm: 40, md: 44 },
                height: { xs: 36, sm: 40, md: 44 },
                boxShadow: `0 ${isMobile ? '2px 8px' : '4px 12px'} ${alpha(theme.palette.common.black, 0.2)}`,
                '&:hover': { bgcolor: theme.palette.common.white, transform: 'rotate(90deg)' },
                '&:active': { transform: 'scale(0.9)' },
                transition: 'all 0.3s ease'
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
            </IconButton>
            <Box
              component="img"
              src={selectedImage}
              alt="Product"
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: { xs: '95vh', sm: '90vh' },
                objectFit: 'contain',
                borderRadius: { xs: '16px', sm: '20px', md: '24px' }
              }}
            />
          </Box>
        </Fade>
      </Modal>

      {/* ─── Snackbar Notifications ─── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: { xs: 2, sm: 3 },
            boxShadow: 4,
            minWidth: { xs: 200, sm: 300 },
            fontSize: { xs: '0.8rem', sm: '0.95rem' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}